/**
 * SISTEMA DE ANIMAÇÕES - BARBA BARÃO
 *
 * Animações criadas seguindo as 10 Heurísticas de Nielsen:
 * - Feedback visual consistente
 * - Controle do usuário (pode interromper com scroll)
 * - Movimento masculino e firme (sem exageros)
 */

import { Variants } from 'framer-motion';

// Duração padrão: firme e controlada (não muito rápida, não muito lenta)
export const durations = {
  fast: 0.2,
  normal: 0.4,
  slow: 0.6,
  verySlow: 0.8,
};

// Easings masculinos e firmes
export const easings = {
  smooth: [0.43, 0.13, 0.23, 0.96] as [number, number, number, number],
  firm: [0.6, 0.01, 0.05, 0.95] as [number, number, number, number],
  bounce: [0.68, -0.55, 0.265, 1.55] as [number, number, number, number],
};

/**
 * FADE IN - Entrada suave de elementos
 * Heurística #1: Visibilidade do status do sistema
 */
export const fadeIn: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: durations.normal,
      ease: easings.smooth,
    },
  },
};

/**
 * FADE IN UP - Entrada de baixo para cima
 * Heurística #8: Design estético e minimalista
 */
export const fadeInUp: Variants = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.slow,
      ease: easings.firm,
    },
  },
};

/**
 * FADE IN DOWN - Entrada de cima para baixo
 */
export const fadeInDown: Variants = {
  hidden: {
    opacity: 0,
    y: -40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.slow,
      ease: easings.firm,
    },
  },
};

/**
 * SLIDE IN LEFT - Entrada da esquerda
 */
export const slideInLeft: Variants = {
  hidden: {
    opacity: 0,
    x: -60,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: durations.slow,
      ease: easings.firm,
    },
  },
};

/**
 * SLIDE IN RIGHT - Entrada da direita
 */
export const slideInRight: Variants = {
  hidden: {
    opacity: 0,
    x: 60,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: durations.slow,
      ease: easings.firm,
    },
  },
};

/**
 * SCALE IN - Entrada com escala
 * Heurística #2: Correspondência entre sistema e mundo real
 */
export const scaleIn: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: durations.normal,
      ease: easings.smooth,
    },
  },
};

/**
 * STAGGER CONTAINER - Container para elementos em sequência
 * Heurística #1: Visibilidade do status (feedback progressivo)
 */
export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

/**
 * HOVER SCALE - Efeito hover com escala sutil
 * Heurística #4: Consistência e padrões
 */
export const hoverScale = {
  scale: 1.03,
  transition: {
    duration: durations.fast,
    ease: easings.smooth,
  },
};

/**
 * HOVER LIFT - Efeito hover de elevação
 * Heurística #2: Feedback visual de interação
 */
export const hoverLift = {
  y: -4,
  transition: {
    duration: durations.fast,
    ease: easings.smooth,
  },
};

/**
 * TAP SCALE - Efeito de clique
 * Heurística #1: Feedback imediato ao usuário
 */
export const tapScale = {
  scale: 0.97,
  transition: {
    duration: durations.fast,
    ease: easings.firm,
  },
};

/**
 * SLIDE TRANSITION - Transição de slides no carousel
 * Heurística #3: Controle e liberdade do usuário
 */
export const slideTransition: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
    transition: {
      duration: durations.slow,
      ease: easings.firm,
    },
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
    transition: {
      duration: durations.slow,
      ease: easings.firm,
    },
  }),
};

/**
 * PROGRESS BAR - Animação de barra de progresso
 * Heurística #1: Visibilidade do status do sistema
 */
export const progressBar: Variants = {
  initial: {
    scaleX: 0,
  },
  animate: {
    scaleX: 1,
    transition: {
      duration: 5, // 5 segundos (tempo do slide)
      ease: 'linear',
    },
  },
};

/**
 * MENU DRAWER - Animação do menu mobile
 * Heurística #3: Controle e liberdade do usuário
 */
export const menuDrawer: Variants = {
  closed: {
    x: '100%',
    transition: {
      duration: durations.normal,
      ease: easings.firm,
    },
  },
  open: {
    x: 0,
    transition: {
      duration: durations.normal,
      ease: easings.firm,
    },
  },
};

/**
 * MENU ITEM - Animação de itens do menu
 * Heurística #4: Consistência e padrões
 */
export const menuItem: Variants = {
  closed: {
    x: 50,
    opacity: 0,
  },
  open: (i: number) => ({
    x: 0,
    opacity: 1,
    transition: {
      delay: i * 0.1,
      duration: durations.normal,
      ease: easings.firm,
    },
  }),
};
